/**
 * Framingham 10-year Hard CHD Risk Score (ATP III / NCEP 2002 point system).
 * Matches MDCalc "Framingham Risk Score for Hard Coronary Heart Disease".
 * Validated for ages 20–79 without prior CHD or diabetes.
 */

export type FraminghamSex = "male" | "female";
export type CholesterolUnit = "mgdl" | "mmoll";

export type FraminghamHardChdInputs = {
  sex: FraminghamSex;
  age: number;
  totalCholesterol: number;
  hdlCholesterol: number;
  systolicBp: number;
  treatedBp: boolean;
  smoker: boolean;
  unitSystem: CholesterolUnit;
};

export type FraminghamHardChdResult = {
  points: number;
  riskPct: number;
};

export type FraminghamRiskTier = {
  label: string;
  color: "success" | "warning" | "error";
  description: string;
  key: "low" | "intermediate" | "high";
};

/** mg/dL per mmol/L conversion factor used by MDCalc / clinical labs. */
export const CHOLESTEROL_MGDL_PER_MMOLL = 38.67;

/** LOINC for Framingham ATP III Hard CHD 10-year risk. */
export const FRAMINGHAM_HARD_CHD_LOINC = "65850-0";

export function formatHardChdRiskPct(riskPct: number): string {
  const rounded = Math.round(riskPct);
  if (rounded < 1) return "<1%";
  if (rounded >= 30) return "≥30%";
  return `${rounded}%`;
}

export function convertCholesterol(
  value: number,
  from: CholesterolUnit,
  to: CholesterolUnit,
): number | null {
  if (!Number.isFinite(value) || value <= 0) return null;
  if (from === to) return value;
  if (from === "mgdl" && to === "mmoll") {
    return Number((value / CHOLESTEROL_MGDL_PER_MMOLL).toFixed(2));
  }
  return Math.round(value * CHOLESTEROL_MGDL_PER_MMOLL);
}

function toMgDl(value: number, unitSystem: CholesterolUnit): number {
  if (unitSystem === "mmoll") {
    return Math.round(value * CHOLESTEROL_MGDL_PER_MMOLL);
  }
  return value;
}

function positiveNumber(value: number): number | null {
  return Number.isFinite(value) && value > 0 ? value : null;
}

function maleAgePoints(age: number): number {
  if (age <= 34) return -9;
  if (age <= 39) return -4;
  if (age <= 44) return 0;
  if (age <= 49) return 3;
  if (age <= 54) return 6;
  if (age <= 59) return 8;
  if (age <= 64) return 10;
  if (age <= 69) return 11;
  if (age <= 74) return 12;
  return 13;
}

function femaleAgePoints(age: number): number {
  if (age <= 34) return -7;
  if (age <= 39) return -3;
  if (age <= 44) return 0;
  if (age <= 49) return 3;
  if (age <= 54) return 6;
  if (age <= 59) return 8;
  if (age <= 64) return 10;
  if (age <= 69) return 12;
  if (age <= 74) return 14;
  return 16;
}

function maleTcPoints(age: number, tc: number): number {
  if (age >= 20 && age <= 39) {
    if (tc < 160) return 0;
    if (tc <= 199) return 4;
    if (tc <= 239) return 7;
    if (tc <= 279) return 9;
    return 11;
  }
  if (age <= 49) {
    if (tc < 160) return 0;
    if (tc <= 199) return 3;
    if (tc <= 239) return 5;
    if (tc <= 279) return 6;
    return 8;
  }
  if (age <= 59) {
    if (tc < 160) return 0;
    if (tc <= 199) return 2;
    if (tc <= 239) return 3;
    if (tc <= 279) return 4;
    return 5;
  }
  if (age <= 69) {
    if (tc < 160) return 0;
    if (tc <= 199) return 1;
    if (tc <= 239) return 1;
    if (tc <= 279) return 2;
    return 3;
  }
  if (tc < 160) return 0;
  if (tc <= 199) return 0;
  if (tc <= 239) return 0;
  if (tc <= 279) return 1;
  return 1;
}

function femaleTcPoints(age: number, tc: number): number {
  if (age >= 20 && age <= 39) {
    if (tc < 160) return 0;
    if (tc <= 199) return 4;
    if (tc <= 239) return 8;
    if (tc <= 279) return 11;
    return 13;
  }
  if (age <= 49) {
    if (tc < 160) return 0;
    if (tc <= 199) return 3;
    if (tc <= 239) return 6;
    if (tc <= 279) return 8;
    return 10;
  }
  if (age <= 59) {
    if (tc < 160) return 0;
    if (tc <= 199) return 2;
    if (tc <= 239) return 4;
    if (tc <= 279) return 5;
    return 7;
  }
  if (age <= 69) {
    if (tc < 160) return 0;
    if (tc <= 199) return 1;
    if (tc <= 239) return 2;
    if (tc <= 279) return 3;
    return 4;
  }
  if (tc < 160) return 0;
  if (tc <= 199) return 1;
  if (tc <= 239) return 1;
  if (tc <= 279) return 2;
  return 2;
}

function maleSmokePoints(age: number, smoker: boolean): number {
  if (!smoker) return 0;
  if (age <= 39) return 8;
  if (age <= 49) return 5;
  if (age <= 59) return 3;
  if (age <= 69) return 1;
  return 1;
}

function femaleSmokePoints(age: number, smoker: boolean): number {
  if (!smoker) return 0;
  if (age <= 39) return 9;
  if (age <= 49) return 7;
  if (age <= 59) return 4;
  if (age <= 69) return 2;
  return 1;
}

function hdlPoints(hdl: number): number {
  if (hdl >= 60) return -1;
  if (hdl >= 50) return 0;
  if (hdl >= 40) return 1;
  return 2;
}

function maleSbpPoints(sbp: number, treatedBp: boolean): number {
  if (!treatedBp) {
    if (sbp < 120) return 0;
    if (sbp <= 129) return 0;
    if (sbp <= 139) return 1;
    if (sbp <= 159) return 1;
    return 2;
  }
  if (sbp < 120) return 0;
  if (sbp <= 129) return 1;
  if (sbp <= 139) return 2;
  if (sbp <= 159) return 2;
  return 3;
}

function femaleSbpPoints(sbp: number, treatedBp: boolean): number {
  if (!treatedBp) {
    if (sbp < 120) return 0;
    if (sbp <= 129) return 1;
    if (sbp <= 139) return 2;
    if (sbp <= 159) return 3;
    return 4;
  }
  if (sbp < 120) return 0;
  if (sbp <= 129) return 3;
  if (sbp <= 139) return 4;
  if (sbp <= 159) return 5;
  return 6;
}

function maleRiskPct(points: number): number {
  if (points < 0) return 0;
  if (points <= 4) return 1;
  if (points === 5) return 2;
  if (points === 6) return 2;
  if (points === 7) return 3;
  if (points === 8) return 4;
  if (points === 9) return 5;
  if (points === 10) return 6;
  if (points === 11) return 8;
  if (points === 12) return 10;
  if (points === 13) return 12;
  if (points === 14) return 16;
  if (points === 15) return 20;
  if (points === 16) return 25;
  return 30;
}

function femaleRiskPct(points: number): number {
  if (points < 9) return 0;
  if (points <= 12) return 1;
  if (points <= 14) return 2;
  if (points === 15) return 3;
  if (points === 16) return 4;
  if (points === 17) return 5;
  if (points === 18) return 6;
  if (points === 19) return 8;
  if (points === 20) return 11;
  if (points === 21) return 14;
  if (points === 22) return 17;
  if (points === 23) return 22;
  if (points === 24) return 27;
  return 30;
}

export function calculateHardChdRisk(
  inputs: FraminghamHardChdInputs,
): FraminghamHardChdResult | null {
  const age = positiveNumber(Number(inputs.age));
  const tcRaw = positiveNumber(Number(inputs.totalCholesterol));
  const hdlRaw = positiveNumber(Number(inputs.hdlCholesterol));
  const sbp = positiveNumber(Number(inputs.systolicBp));
  if (age == null || tcRaw == null || hdlRaw == null || sbp == null) {
    return null;
  }
  if (age < 20 || age > 79) {
    return null;
  }

  const tc = toMgDl(tcRaw, inputs.unitSystem);
  const hdl = toMgDl(hdlRaw, inputs.unitSystem);

  if (inputs.sex === "male") {
    const points =
      maleAgePoints(age) +
      maleTcPoints(age, tc) +
      maleSmokePoints(age, inputs.smoker) +
      hdlPoints(hdl) +
      maleSbpPoints(sbp, inputs.treatedBp);
    return { points, riskPct: maleRiskPct(points) };
  }

  const points =
    femaleAgePoints(age) +
    femaleTcPoints(age, tc) +
    femaleSmokePoints(age, inputs.smoker) +
    hdlPoints(hdl) +
    femaleSbpPoints(sbp, inputs.treatedBp);
  return { points, riskPct: femaleRiskPct(points) };
}

export function riskTierFromPct(riskPct: number): FraminghamRiskTier {
  if (riskPct < 10) {
    return {
      key: "low",
      label: "Low Risk (<10%)",
      color: "success",
      description: "10-year risk of hard coronary heart disease is low.",
    };
  }
  if (riskPct <= 20) {
    return {
      key: "intermediate",
      label: "Intermediate Risk (10-20%)",
      color: "warning",
      description:
        "10-year risk of hard coronary heart disease is moderate / intermediate.",
    };
  }
  return {
    key: "high",
    label: "High Risk (>20%)",
    color: "error",
    description:
      "10-year risk of hard coronary heart disease is high. Aggressive primary prevention recommended.",
  };
}
